import React, { useState, useEffect, useCallback } from 'react';
import { Settings, Save, RefreshCw, AlertTriangle, CheckCircle, Info, Percent } from 'lucide-react';

/**
 * Threshold Settings View
 * Admin-only view for configuring discount approval thresholds
 */
const ThresholdSettingsView = () => {
  const [thresholds, setThresholds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const categoryLabels = {
    platform: 'Platform (SplashBI/SplashAI)',
    users: 'User Licenses',
    reports: 'Reports & Dashboards',
    peopleAnalytics: 'People Analytics',
    services: 'Professional Services',
    overall: 'Overall Quote Discount'
  };

  const categoryDescriptions = {
    platform: 'Maximum discount allowed on platform subscription fees',
    users: 'Maximum discount allowed on user license fees',
    reports: 'Maximum discount allowed on reports and dashboard products',
    peopleAnalytics: 'Maximum discount allowed on People Analytics modules',
    services: 'Maximum discount allowed on implementation and training services',
    overall: 'Maximum combined discount on the total quote value'
  };

  const fetchThresholds = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const response = await fetch(`${API_URL}/api/admin/thresholds`, {
        headers,
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setThresholds(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching thresholds:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchThresholds();
  }, [fetchThresholds]);

  const handleThresholdChange = (category, value) => {
    const numValue = Math.max(0, Math.min(100, parseInt(value) || 0));
    setThresholds(prev =>
      prev.map(t => t.category === category ? { ...t, max_discount_percent: numValue } : t)
    );
    setHasChanges(true);
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/api/admin/thresholds`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        credentials: 'include',
        body: JSON.stringify({
          thresholds: thresholds.map(t => ({
            category: t.category,
            max_discount_percent: t.max_discount_percent
          }))
        })
      });

      if (response.ok) {
        setHasChanges(false);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        alert('Failed to save thresholds');
      }
    } catch (error) {
      console.error('Error saving thresholds:', error);
      alert('Failed to save thresholds');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    fetchThresholds();
    setHasChanges(false);
  };

  // Order thresholds for display
  const orderedCategories = ['platform', 'users', 'reports', 'peopleAnalytics', 'services', 'overall'];
  const orderedThresholds = orderedCategories
    .map(cat => thresholds.find(t => t.category === cat))
    .filter(Boolean);

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-800 mb-2 flex items-center gap-3">
          <Settings className="text-blue-600" size={32} />
          Discount Thresholds
        </h1>
        <p className="text-slate-600">
          Configure maximum discount percentages before approval is required
        </p>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
        <div className="flex gap-3">
          <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">How Thresholds Work</p>
            <p className="text-blue-700">
              When a sales rep submits a quote with discounts exceeding these thresholds,
              the quote will require manager/admin approval before it can be finalized.
              Quotes within thresholds are automatically approved.
            </p>
          </div>
        </div>
      </div>

      {/* Thresholds Card */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
            Loading thresholds...
          </div>
        ) : (
          <>
            {/* Thresholds List */}
            <div className="divide-y divide-slate-100">
              {orderedThresholds.map((threshold) => (
                <div key={threshold.category} className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-800 mb-1">
                        {categoryLabels[threshold.category] || threshold.category}
                      </h3>
                      <p className="text-sm text-slate-500">
                        {categoryDescriptions[threshold.category]}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={threshold.max_discount_percent}
                          onChange={(e) => handleThresholdChange(threshold.category, e.target.value)}
                          className="w-20 px-3 py-2 pr-8 border border-slate-300 rounded-lg text-right font-semibold focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <Percent size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      </div>
                    </div>
                  </div>

                  {/* Visual indicator */}
                  <div className="mt-3">
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all ${
                          threshold.max_discount_percent > 30
                            ? 'bg-red-400'
                            : threshold.max_discount_percent > 20
                            ? 'bg-amber-400'
                            : 'bg-green-400'
                        }`}
                        style={{ width: `${threshold.max_discount_percent}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-slate-400 mt-1">
                      <span>0%</span>
                      <span>50%</span>
                      <span>100%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer with Actions */}
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {hasChanges && (
                  <span className="flex items-center gap-1 text-amber-600 text-sm">
                    <AlertTriangle size={16} />
                    Unsaved changes
                  </span>
                )}
                {saveSuccess && (
                  <span className="flex items-center gap-1 text-green-600 text-sm">
                    <CheckCircle size={16} />
                    Changes saved successfully
                  </span>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleReset}
                  disabled={!hasChanges || saving}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <RefreshCw size={16} />
                  Reset
                </button>
                <button
                  onClick={handleSave}
                  disabled={!hasChanges || saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {saving ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  ) : (
                    <Save size={16} />
                  )}
                  Save Changes
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Threshold Legend */}
      <div className="mt-6 bg-slate-50 rounded-xl p-4">
        <h4 className="text-sm font-semibold text-slate-700 mb-3">Threshold Indicators</h4>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 bg-green-400 rounded" />
            <span className="text-slate-600">Low (0-20%): Auto-approved</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 bg-amber-400 rounded" />
            <span className="text-slate-600">Medium (21-30%): Consider review</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 bg-red-400 rounded" />
            <span className="text-slate-600">High (31%+): Strict oversight</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThresholdSettingsView;
